import {
  Box,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AuthenticateIcon from "assets/svg/AuthenticateIcon";
import CloseIcon from "assets/svg/CloseIcon";
import { AxiosError } from "axios";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { FETCH_PROFILE_TYPE } from "reduxStore/actions/authActionType";
import { TwoFATokenRequest } from "reduxStore/dataTypes";
import { enable2FA } from "services/apiService";
import theme, { responsiveFontSize } from "themes/Theme";

interface IProps {
  open: boolean;
  handleClose: () => void;
  onSuccess: () => void;
}

const EnableTwoFA: React.FC<IProps> = ({ open, handleClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(0);
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return; // Allow only numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to the next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter" && otp.join("").length === 6) {
      handleEnable2FA();
    }
  };

  const handleEnable2FA = async () => {
    try {
      const body: TwoFATokenRequest = {
        token: otp.join(""),
      };
      const response = await enable2FA(body);
      if (response && response.status === 200) {
        toast.success(response?.data?.message);
        setOtp(new Array(6).fill(""));
        dispatch({ type: FETCH_PROFILE_TYPE });
        onSuccess();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message);
        setOtp(new Array(6).fill(""));
      }
      console.log("Error: ", error);
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        handleClose();
      }}
      aria-labelledby="change-email-modal"
      aria-describedby="change-email-description"
    >
      <Box sx={styles.modalContainer}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box />
          <Box sx={styles.iconContainer}>
            <AuthenticateIcon />
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography sx={styles.titleText}>Enter 6 Digit Code</Typography>
        <Typography sx={styles.descriptionText}>
          Enter the 6 digit code from your authenticator app to enable 2-factor
          authentication
        </Typography>
        <Stack direction={"row"} alignItems={"center"} gap={2} mb={3}>
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              variant="outlined"
              size="small"
              slotProps={{
                input: {
                  style: {
                    fontSize: "20px",
                    backgroundColor: "rgba(15, 80, 81, 0.39)",
                  },
                },
              }}
              sx={{
                width: "50px",
                height: "50px",
                "& input": {
                  textAlign: "center", // Centering the text inside the input
                },
              }}
            />
          ))}
        </Stack>
        <PrimaryButton title="Enable 2FA" onClick={handleEnable2FA} />
      </Box>
    </Modal>
  );
};
export default EnableTwoFA;
const styles = {
  modalContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    minWidth: 400,
    maxWidth: { xs: window.innerWidth * 0.8, md: window.innerWidth * 0.5 },
    bgcolor: "#FFFFFF",
    borderRadius: "2em",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    px: 4,
    py: 2,
  },
  iconContainer: {
    width: "52px",
    height: "52px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid #D9D9D9",
    borderRadius: "8px",
    aspectRatio: "1 / 1",
  },
  titleText: {
    fontSize: responsiveFontSize(20),
    fontWidth: 400,
    color: theme.palette.text.primary,
    marginTop: "1em",
  },
  descriptionText: {
    fontSize: responsiveFontSize(14),
    fontWidth: 400,
    color: theme.palette.text.secondary,
    marginTop: "1em",
    marginBottom: "1em",
  },
  scanText: {
    fontSize: responsiveFontSize(15),
    fontWidth: 400,
    color: theme.palette.primary.main,
  },
};
